import { Popover as BasePopover } from "@base-ui/react/popover";
import type { ReactElement, ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { FLOATING_PANEL_CLASS, FLOATING_POSITIONER_CLASS, type FloatingAlign, type FloatingSide } from "./FloatingPanel";

/**
 * A floating panel of any content, anchored to its trigger. Opens on click, or on hover with openOnHover.
 *
 * <Popover trigger={<Button text="Send Test" />} align="start" className="w-80">
 *     <SendTestForm />
 * </Popover>
 */
export const Popover = (props: {
    /** Rendered as the button that opens the popover. Must forward its ref and props to the DOM. */
    trigger: ReactElement;
    children: ReactNode;
    /** Defaults to bottom. */
    side?: FloatingSide;
    /** Defaults to center. */
    align?: FloatingAlign;
    /** In pixels. Defaults to 4. */
    sideOffset?: number;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    /** Opens on hover as well as click, as for a preview card. */
    openOnHover?: boolean;
    /** Milliseconds before opening on hover. Requires openOnHover. */
    delay?: number;
    /** Milliseconds before closing after the pointer leaves. Requires openOnHover. */
    closeDelay?: number;
    /** The popover's accessible name, when its content has no heading. */
    ariaLabel?: string;
    /** Sizing and padding for the panel. */
    className?: string;
}) => {
    return (
        <BasePopover.Root open={props.open} onOpenChange={open => props.onOpenChange?.(open)}>
            <BasePopover.Trigger
                render={props.trigger}
                openOnHover={props.openOnHover}
                delay={props.delay}
                closeDelay={props.closeDelay}
            />
            <BasePopover.Portal>
                <BasePopover.Positioner
                    className={FLOATING_POSITIONER_CLASS}
                    side={props.side ?? "bottom"}
                    align={props.align ?? "center"}
                    sideOffset={props.sideOffset ?? 4}
                >
                    <BasePopover.Popup className={twMerge(FLOATING_PANEL_CLASS, props.className)} aria-label={props.ariaLabel}>
                        {props.children}
                    </BasePopover.Popup>
                </BasePopover.Positioner>
            </BasePopover.Portal>
        </BasePopover.Root>
    );
};
